import type { Dayjs } from 'dayjs'

/**
 * Build API `parameters` object from form values for a given constraint type.
 */
export const buildConstraintParameters = (
  typeCode: string,
  values: Record<string, unknown>
): Record<string, unknown> => {
  switch (typeCode) {
    case 'venue_blackout': {
      const dates = values.dates as Dayjs[] | undefined
      const parameters: Record<string, unknown> = {
        dates: (() => {
          if (!dates || dates.length < 2) {
            return dates?.map((d: Dayjs) => d.format('YYYY-MM-DD')) || []
          }
          const start = dates[0]
          const end = dates[1]
          const result: string[] = []
          let current = start.startOf('day')
          const endDay = end.startOf('day')
          while (current.isBefore(endDay) || current.isSame(endDay, 'day')) {
            result.push(current.format('YYYY-MM-DD'))
            current = current.add(1, 'day')
          }
          return result
        })(),
      }
      const venueUids = values.venue_uids as string[] | undefined
      if (venueUids && venueUids.length > 0) {
        parameters.venue_uids = venueUids
      }
      const facilityUids = values.facility_uids as string[] | undefined
      if (facilityUids && facilityUids.length > 0) {
        parameters.facility_uids = facilityUids
      }
      return parameters
    }

    case 'venue_capacity':
      return {
        facility_uids: values.facility_uids,
        max_concurrent_games: values.max_concurrent_games,
      }

    case 'time_window': {
      const params: Record<string, unknown> = {
        start_time: (values.start_time as Dayjs)?.format('HH:mm'),
        end_time: (values.end_time as Dayjs)?.format('HH:mm'),
      }
      const days = values.days as number[] | undefined
      if (days && days.length > 0) {
        params.days = days
      }
      return params
    }

    case 'team_bye_period': {
      const range = values.bye_date_range as Dayjs[] | undefined
      return {
        start_date: range?.[0]?.format('YYYY-MM-DD'),
        end_date: range?.[1]?.format('YYYY-MM-DD'),
      }
    }

    case 'no_back_to_back':
      return { min_minutes_between: values.min_minutes_between }

    case 'min_rest_days':
      return { min_days: values.min_days }

    case 'bye_distribution':
      return { max_consecutive_byes: values.max_consecutive_byes }

    case 'home_away_balance':
      return { target_home_percentage: values.target_home_percentage || 50 }

    case 'preferred_day':
      return { days: values.preferred_days || [] }

    case 'restricted_day':
      return { days: values.restricted_days || [] }

    case 'max_consecutive_late_games':
      return {
        late_time_threshold: (values.late_time_threshold as Dayjs)?.format(
          'HH:mm'
        ),
        max_consecutive: values.max_consecutive,
      }

    case 'preferred_time':
      return {
        start_time: (values.pref_start_time as Dayjs)?.format('HH:mm'),
        end_time: (values.pref_end_time as Dayjs)?.format('HH:mm'),
      }

    case 'max_games_per_team_per_day':
      return { max_games: values.max_games }

    case 'games_per_team_per_week':
      return { games_per_week: values.games_per_week }

    default:
      return {}
  }
}

/**
 * Returns default form values for a given constraint type code.
 */
export const getDefaultValues = (typeCode: string): Record<string, unknown> => {
  switch (typeCode) {
    case 'min_rest_days':
      return { min_days: 1 }
    case 'no_back_to_back':
      return { min_minutes_between: 60 }
    case 'max_games_per_team_per_day':
      return { max_games: 2 }
    case 'games_per_team_per_week':
      return { games_per_week: 1 }
    case 'venue_capacity':
      return { max_concurrent_games: 1 }
    case 'bye_distribution':
      return { max_consecutive_byes: 2 }
    case 'home_away_balance':
      return { target_home_percentage: 50 }
    case 'max_consecutive_late_games':
      return { max_consecutive: 2 }
    default:
      return {}
  }
}

/**
 * Imperative validation for constraint form values.
 * Returns an error message string, or null if valid.
 */
export const validateConstraintValues = (
  typeCode: string,
  values: Record<string, unknown>
): string | null => {
  switch (typeCode) {
    case 'venue_blackout': {
      const dates = values.dates as unknown[] | undefined
      if (!dates || dates.length === 0) {
        return 'At least one date is required'
      }
      return null
    }
    case 'venue_capacity': {
      const facilityUids = values.facility_uids as string[] | undefined
      if (!facilityUids || facilityUids.length === 0) {
        return 'At least one facility is required'
      }
      if (!values.max_concurrent_games) {
        return 'Max concurrent games is required'
      }
      return null
    }
    case 'no_back_to_back':
      if (!values.min_minutes_between) {
        return 'Minimum time between games is required'
      }
      return null
    case 'min_rest_days':
      if (!values.min_days) {
        return 'Minimum rest days is required'
      }
      return null
    case 'team_bye_period': {
      const range = values.bye_date_range as unknown[] | undefined
      if (!range || range.length < 2) {
        return 'Date range is required'
      }
      return null
    }
    case 'preferred_day': {
      const days = values.preferred_days as number[] | undefined
      if (!days || days.length === 0) {
        return 'At least one day is required'
      }
      return null
    }
    case 'restricted_day': {
      const rDays = values.restricted_days as number[] | undefined
      if (!rDays || rDays.length === 0) {
        return 'At least one day is required'
      }
      return null
    }
    case 'max_consecutive_late_games': {
      if (!values.late_time_threshold) {
        return 'Late game threshold time is required'
      }
      if (!values.max_consecutive) {
        return 'Max consecutive late games is required'
      }
      return null
    }
    case 'max_games_per_team_per_day':
      if (!values.max_games) {
        return 'Max games per day is required'
      }
      return null
    case 'games_per_team_per_week':
      if (!values.games_per_week) {
        return 'Games per week is required'
      }
      return null
    default:
      return null
  }
}
